import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { syncRevenueFromHubspot } from '../data/goalsApi.js'
import { useRevenueSyncStore } from '../store/revenueSyncStore.js'

/**
 * useRevenueAutoSync — kick off a HubSpot revenue sync on app mount
 * and every 10 minutes thereafter.
 *
 * Mount once at the app root. State lives in `useRevenueSyncStore`
 * so any surface (e.g. the Revenue pillar header) can render
 * "Syncing…" / "Last synced 2m ago" / "Sync failed: …" without this
 * hook needing to know about them.
 *
 * Concurrency: the store's `runSync` guard means a 10-minute poll
 * tick that overlaps with a manual "Sync now" click is a no-op, not
 * a duplicate network call.
 *
 * Errors: surfaced in the store — we don't retry automatically, the
 * next poll interval covers transient failures.
 */
const TEN_MINUTES = 10 * 60 * 1000

export function useRevenueAutoSync() {
  const runSync = useRevenueSyncStore((s) => s.runSync)
  const queryClient = useQueryClient()
  // Guard so StrictMode's double-mount doesn't stack two pollers.
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const trigger = async () => {
      try {
        await runSync(syncRevenueFromHubspot)
        // Pull the updated goals into the local cache on success
        queryClient.invalidateQueries({ queryKey: ['goals'] })
      } catch (err) {
        // Swallow — store already captured the error for UI display
        console.warn('[revenue-sync] auto-sync failed', err?.message)
      }
    }

    // First sync a moment after mount so the initial goals fetch can
    // land first — easier to visually verify the update replaced
    // stale numbers.
    const initial = setTimeout(trigger, 1500)
    const interval = setInterval(trigger, TEN_MINUTES)

    return () => {
      clearTimeout(initial)
      clearInterval(interval)
    }
  }, [runSync, queryClient])
}
